import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AccountModal from './AccountModal';
import { useUI } from '../../context/UIContext';
import { subscribeToPushNotifications, unsubscribeFromPushNotifications } from '../../utils/pushNotifications';

const Header = () => {
    const { user, hasRole, logout } = useAuth();
    const [showAccount, setShowAccount] = useState(false);
    const { theme, toggleTheme, isSidebarCollapsed, toggleSidebar } = useUI();
    const location = useLocation();
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [pushEnabled, setPushEnabled] = useState(false);

    useEffect(() => {
        const checkSubscription = async () => {
            if ('serviceWorker' in navigator && 'PushManager' in window) {
                const registration = await navigator.serviceWorker.getRegistration();
                if (registration) {
                    const subscription = await registration.pushManager.getSubscription();
                    setPushEnabled(!!subscription);
                }
            }
        };
        checkSubscription();
    }, []);

    const handleTogglePush = async () => {
        if (!user) return;

        if (pushEnabled) {
            // Unsubscribe
            const success = await unsubscribeFromPushNotifications();
            if (success) {
                setPushEnabled(false);
            }
        } else {
            // Subscribe
            const success = await subscribeToPushNotifications(user.id, user.companyId);
            if (success) {
                setPushEnabled(true);
            }
        }
    };

    return (
        <header className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-background-dark px-6 py-3 sticky top-0 z-50 h-[65px]">
            <div className="flex items-center gap-8">
                <div className="flex items-center gap-4">
                    <button
                        onClick={toggleSidebar}
                        className="flex items-center gap-3 text-primary hover:opacity-80 transition-all active:scale-95 group"
                        title={isSidebarCollapsed ? "Agrandir le menu" : "Réduire le menu"}
                    >
                        <div className="flex items-center gap-2">
                            <div className="bg-white rounded-xl p-1.5 shadow-sm flex items-center justify-center border border-slate-200 dark:border-white/10">
                                <img src="/Stockizio.png" alt="Stockizio Logo" className="h-7 w-auto object-contain" />
                            </div>
                            <span className="text-xl font-bold tracking-tight">
                                <span className="text-black dark:text-white">Stock</span>
                                <span style={{ color: "#E8642B" }}>izio</span>
                            </span>
                        </div>
                    </button>
                </div>
                <div className="hidden md:flex items-center gap-6">
                    <Link to="/dashboard" className={`text-sm font-semibold transition-colors ${location.pathname === '/dashboard' ? 'text-primary' : 'text-slate-600 dark:text-slate-400 hover:text-primary'}`}>Tableau de bord</Link>
                    <Link to="/stocks" className={`text-sm font-medium transition-colors ${location.pathname === '/stocks' ? 'text-primary font-semibold' : 'text-slate-600 dark:text-slate-400 hover:text-primary'}`}>Stocks</Link>
                    <Link to="/analytics" className={`text-sm font-medium transition-colors ${location.pathname === '/analytics' ? 'text-primary font-semibold' : 'text-slate-600 dark:text-slate-400 hover:text-primary'}`}>Rapports</Link>
                    {hasRole('admin') && (
                        <>
                            <Link to="/users" className={`text-sm font-medium transition-colors ${location.pathname === '/users' ? 'text-primary font-semibold' : 'text-slate-600 dark:text-slate-400 hover:text-primary'}`}>Utilisateurs</Link>
                            <Link to="/admin/settings" className={`text-sm font-medium transition-colors ${location.pathname === '/admin/settings' ? 'text-primary font-semibold' : 'text-slate-600 dark:text-slate-400 hover:text-primary'}`}>Configuration</Link>
                        </>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-4">
                <div className="relative hidden sm:block">
                    <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xl">search</span>
                    <input
                        className="w-64 bg-slate-100 dark:bg-slate-800 border-none rounded-lg py-2 pl-10 pr-4 text-sm focus:ring-2 focus:ring-primary outline-none text-slate-900 dark:text-white"
                        placeholder="Rechercher un produit..."
                        type="text"
                    />
                </div>

                <button
                    onClick={toggleTheme}
                    className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                    title={theme === 'dark' ? 'Passer au mode clair' : 'Passer au mode sombre'}
                >
                    <span className="material-symbols-outlined">
                        {theme === 'dark' ? 'light_mode' : 'dark_mode'}
                    </span>
                </button>

                {'Notification' in window && (
                    <button
                        onClick={handleTogglePush}
                        className={`p-2 rounded-lg transition-colors relative ${pushEnabled ? 'text-primary bg-primary/10' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
                        title={pushEnabled ? 'Désactiver les notifications push' : 'Activer les notifications push'}
                    >
                        <span className="material-symbols-outlined">
                            {pushEnabled ? 'notifications_active' : 'notifications'}
                        </span>
                        {!pushEnabled && (
                            <span className="absolute top-2 right-2 flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 border-2 border-white dark:border-slate-900"></span>
                            </span>
                        )}
                    </button>
                )}

                <div
                    className="relative"
                    onMouseEnter={() => setShowProfileMenu(true)}
                    onMouseLeave={() => setShowProfileMenu(false)}
                >
                    <div className="h-9 w-9 rounded-full bg-primary/20 flex items-center justify-center border border-primary/30 cursor-pointer overflow-hidden hover:ring-2 hover:ring-primary/50 transition-all group" title={user?.email}>
                        {user?.avatar ? (
                            <img src={user.avatar} alt="Avatar" className="w-full h-full object-cover" />
                        ) : user?.name ? (
                            <span className="text-primary font-bold text-xs uppercase">{user.name.charAt(0)}</span>
                        ) : (
                            <span className="material-symbols-outlined text-primary text-sm">person</span>
                        )}
                        <span className="absolute -bottom-0.5 -right-0.5 size-3 bg-emerald-500 border-2 border-white dark:border-background-dark rounded-full"></span>
                    </div>

                    {showProfileMenu && (
                        <div className="absolute right-0 top-full pt-2 z-50">
                            <div className="w-56 bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 py-2 animate-fade-in">
                                <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 mb-2">
                                    <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{user?.name}</p>
                                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                                </div>

                                <button type="button" onClick={() => { setShowAccount(true); setShowProfileMenu(false); }} className="w-full px-4 py-2.5 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-3 transition-colors">
                                    <span className="material-symbols-outlined text-[18px]">person</span>
                                    Mon Profil
                                </button>

                                {hasRole('admin') && (
                                    <Link to="/admin/company" className="w-full px-4 py-2.5 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-3 transition-colors">
                                        <span className="material-symbols-outlined text-[18px]">domain</span>
                                        Ma Société
                                    </Link>
                                )}

                                <div className="my-2 border-t border-slate-100 dark:border-slate-800"></div>

                                <button onClick={logout} className="w-full px-4 py-2.5 text-left text-sm text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 flex items-center gap-3 transition-colors font-semibold">
                                    <span className="material-symbols-outlined text-[18px]">logout</span>
                                    Déconnexion
                                </button>
                            </div>
                        </div>
                    )}
                    <AccountModal open={showAccount} onClose={() => setShowAccount(false)} />
                </div>
            </div>
        </header>
    );
};

export default Header;
